import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';
import { SendCloudShippingMethod } from '@/models/sendcloud';
import { PickupPoint } from '@/models';

/** Sendcloud v2 liste des méthodes : pays avec `iso_2`, pas toujours `code` */
function normalizeSendcloudMethod(raw: Record<string, unknown>): SendCloudShippingMethod {
  const countriesRaw = raw.countries;
  const countries: SendCloudShippingMethod['countries'] = Array.isArray(countriesRaw)
    ? (countriesRaw as Record<string, unknown>[]).map(c => {
        const code = String(c.iso_2 ?? c.code ?? '').trim().toUpperCase();
        const priceVal = c.price;
        const price =
          typeof priceVal === 'string' ? Number.parseFloat(priceVal) : Number(priceVal) || 0;
        return {
          code,
          name: String(c.name ?? code),
          price,
          lead_time_hours:
            typeof c.lead_time_hours === 'number' ? c.lead_time_hours : undefined,
        };
      }).filter(c => c.code.length > 0)
    : [];

  const minW = raw.min_weight ?? raw.minWeight;
  const maxW = raw.max_weight ?? raw.maxWeight;

  /**
   * Sendcloud renvoie min_weight / max_weight en grammes (ex. 7000–10000 pour 7–10 kg).
   * Les valeurs en kg restent typiquement sous 100 pour un colis standard.
   */
  const normalizeSendcloudMethodWeightKg = (value: unknown): number => {
    const n = typeof value === 'string' ? Number.parseFloat(String(value).trim()) : Number(value);
    if (!Number.isFinite(n) || n <= 0) return 0;
    if (n >= 100) return n / 1000;
    return n;
  };

  return {
    id: Number(raw.id),
    name: String(raw.name ?? ''),
    carrier: String(raw.carrier ?? ''),
    description: raw.description != null ? String(raw.description) : undefined,
    countries,
    minWeight: normalizeSendcloudMethodWeightKg(minW),
    maxWeight: normalizeSendcloudMethodWeightKg(maxW),
    servicePointInput:
      raw.service_point_input != null
        ? String(raw.service_point_input)
        : raw.servicePointInput != null
          ? String(raw.servicePointInput)
          : undefined,
    isActive: raw.is_active === true || raw.isActive === true,
  };
}

function normalizeSendcloudMethodsList(rawList: unknown[]): SendCloudShippingMethod[] {
  return rawList
    .filter((m): m is Record<string, unknown> => m != null && typeof m === 'object')
    .map(normalizeSendcloudMethod);
}

export interface RelayPointResult {
  id: string;
  code?: string;
  name: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  latitude: number;
  longitude: number;
  openingHours?: string;
  /** Renvoyé par la Cloud Function (`mondial_relay` | `sendcloud`) */
  provider?: string;
}

export class ShippingService {
  /**
   * Browse all Sendcloud platform methods (admin use, CF uses platform keys)
   */
  /**
   * Catalogue Sendcloud du compte marchand (clés BYOK). Requiert orgId.
   */
  static async browseSendcloudMethodsMerchant(organizationId: string): Promise<SendCloudShippingMethod[]> {
    const q = new URLSearchParams({ orgId: organizationId });
    const response = await fetch(`${FUNCTIONS_BASE_URL}/browseSendcloudMethodsMerchant?${q.toString()}`);
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg = typeof errBody?.error === 'string' ? errBody.error : `HTTP ${response.status}`;
      throw new Error(msg);
    }
    const data: unknown = await response.json();
    if (Array.isArray(data)) {
      return normalizeSendcloudMethodsList(data);
    }
    if (data && typeof data === 'object') {
      const o = data as Record<string, unknown>;
      if (Array.isArray(o.shipping_methods)) {
        return normalizeSendcloudMethodsList(o.shipping_methods);
      }
      if (Array.isArray(o.methods)) {
        return normalizeSendcloudMethodsList(o.methods);
      }
    }
    console.warn('[ShippingService.browseSendcloudMethodsMerchant] format inattendu:', data);
    return [];
  }

  static async browseSendcloudMethods(): Promise<SendCloudShippingMethod[]> {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/browseSendcloudMethods`);
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg = typeof errBody?.error === 'string' ? errBody.error : `HTTP ${response.status}`;
      throw new Error(msg);
    }
    const data: unknown = await response.json();
    // Sendcloud v2 renvoie { shipping_methods: [...] }, pas un tableau brut
    if (Array.isArray(data)) {
      return normalizeSendcloudMethodsList(data);
    }
    if (data && typeof data === 'object') {
      const o = data as Record<string, unknown>;
      if (Array.isArray(o.shipping_methods)) {
        return normalizeSendcloudMethodsList(o.shipping_methods);
      }
      if (Array.isArray(o.methods)) {
        return normalizeSendcloudMethodsList(o.methods);
      }
    }
    console.warn('[ShippingService.browseSendcloudMethods] format de réponse inattendu:', data);
    return [];
  }

  static async testSendcloudApiKeys(params: {
    organizationId: string;
    publicKey?: string;
    secretKey?: string;
  }): Promise<{ success: boolean; error?: string; message?: string; methodCount?: number }> {
    const body: Record<string, string> = { organizationId: params.organizationId };
    if (params.publicKey?.trim()) body.publicKey = params.publicKey.trim();
    if (params.secretKey?.trim()) body.secretKey = params.secretKey.trim();

    const response = await fetch(`${FUNCTIONS_BASE_URL}/validateSendcloudApiKeys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.success === false) {
      return {
        success: false,
        error: typeof data?.error === 'string' ? data.error : `HTTP ${response.status}`,
      };
    }
    return {
      success: true,
      message: typeof data?.message === 'string' ? data.message : undefined,
      methodCount: typeof data?.methodCount === 'number' ? data.methodCount : undefined,
    };
  }

  static async saveSendcloudMerchantKeys(params: {
    organizationId: string;
    publicKey: string;
    secretKey: string;
  }): Promise<{ success: boolean; error?: string }> {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/saveSendcloudSecrets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        error: typeof data?.error === 'string' ? data.error : `HTTP ${response.status}`,
      };
    }
    return { success: true };
  }

  /**
   * Enregistre les identifiants Mondial Relay (mot de passe → Google Secret Manager).
   */
  static async saveMondialRelayMerchantCredentials(params: {
    organizationId: string;
    enseigne: string;
    apiLogin: string;
    customerId?: string;
    /** Si omis alors qu’un mot de passe existe déjà (SM ou legacy), mise à jour des champs seuls. */
    apiPassword?: string;
  }): Promise<{ success: boolean; error?: string; apiPasswordMasked?: string }> {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/saveMondialRelaySecrets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        error: typeof data?.error === 'string' ? data.error : `HTTP ${response.status}`,
      };
    }
    return {
      success: true,
      apiPasswordMasked: typeof data?.apiPasswordMasked === 'string' ? data.apiPasswordMasked : undefined,
    };
  }

  /**
   * Search relay points for a given shipping method via the multi-provider CF
   */
  static async getRelayPoints(
    orgId: string,
    shippingMethodId: string,
    country: string,
    postalCode: string
  ): Promise<PickupPoint[]> {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/getRelayPoints`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgId, shippingMethodId, country, postalCode }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur ${response.status}: ${errorText}`);
    }

    const data: RelayPointResult[] = await response.json();
    return data.map(p => ({
      id: p.id.toString(),
      code: typeof p.code === 'string' ? p.code : undefined,
      name: p.name,
      address: p.address,
      city: p.city,
      postalCode: p.postalCode,
      country: p.country,
      latitude: p.latitude,
      longitude: p.longitude,
      openingHours: p.openingHours,
      provider: p.provider === 'mondial_relay' ? ('mondial_relay' as const) : ('chronopost' as const),
    }));
  }
}
